
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, EventEmitter, Input,
  OnDestroy,
  OnInit, Output, QueryList,
  ViewChild, ViewChildren
} from '@angular/core';
import IFarm = Data.Authentication.IFarm;
import {PDFExportComponent}           from '@progress/kendo-angular-pdf-export';
import {
  MapStore
}                                                                                        from '../../../stores/map/map.store';
import {
  LanguageStore
}                                                                                        from '../../../stores/translation/language.store';
import {
  LoginStore
}                                                                                        from '../../../stores/login/login.store';
import {
  TaskManagementDataProviders
}                                                                                        from '../../../task-management/task-management-data-providers/task-management-data-providers';
import {
  ApDateService
}                                                                                        from '../../../ap-core/services/ap-date-service';
import {
  CropTypeStore
}                                                                                        from '../../../stores/base-data/crop.types.store';
import {
  SettingsStore
}                                                                                        from '../../../stores/base-data/settings.store';
import {
  GetRoundNumericService
}                                                                                        from '../../../ap-utils/service/get-round-numeric.service';
import {
  TranslationStore
}                                                                                        from '../../../stores/translation/translation.store';
import {Subscription}                                                                    from 'rxjs';
import {
  ISampleFieldGridData
}                                                                                        from '../../../field-management/types/sample-fields.types';
import {drawDOM, exportPDF} from '@progress/kendo-drawing';
import {
  saveAs
}                           from '@progress/kendo-file-saver';
import * as _                                                                            from 'lodash';
import ISampleTracks = Data.Nutrients.ISampleTracks;
import {ApElementOxydTypeEnum, ApStatsType, IBarLineAreaChartData} from '../../../ap-interface';
import {
  FieldStatisticHistogramService
}                                                                  from '../../../services/data/field-statistic-histogram.service';
import {
  IChartSet
}                                                from '../../../stores/statistic/statistic.store';
import {ChartComponent, Margin, ValueAxisLabels} from '@progress/kendo-angular-charts';
import {ApBaseVectorLayer} from '../../../ap-map';
import {
  MapFactoryLayer
}                                                                                        from '../../../ap-map/layers/map-factory-layer';
import {APP_CONFIGURATION} from '../../../ap-core/config';
import {SOIL_GROUP_LEGEND} from '../../../ap-map/layers/layer.legends';
import {ILegendValue} from '../../../stores/map/map.legend.store';
import {LandUseStore} from '../../../stores/base-data/landuse.store';
import {NotifyStore} from '../../../stores/dialog/notify.store';
import {ApErrorHandler} from '../../../ap-core/error-handling/ap-errorhandler';
import {ApErrorhandlerService} from '../../../ap-core/error-handling/ap-errorhandler.service';
import {ApElementType} from '../../../ap-interface/enums/ap-elements-type.enum';
import {
  GetElementService
}                                                                                        from '../../../ap-utils/service/ap-get-element.service';
import {ApNutrientService} from '../../../ap-utils/service/ap-nutrient.service';
import {FieldNutrientDistributionStore} from '../../../stores/nutrients/field-nutrient-distributions.store';
import {UnitsStore} from '../../../stores/common/units.store';
import {SampleMethodService} from '../../../services/data/sample.method.service';

@Component({
  selector: 'soil-sample-report',
  templateUrl: './soil-sample-report.component.html',
  styleUrls: ['./soil-sample-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SoilSampleReportComponent implements OnInit, OnDestroy {
  @ViewChild('report', {static: true}) private _report: PDFExportComponent;
  @ViewChildren('histogram') histograms: QueryList<ChartComponent>;
  @Input() itemsFieldLevel: ISampleFieldGridData[] = [];
  @Input() itemsSampleTrackByField: [ISampleTracks[]] = [[]];
  @Output() exportDone = new EventEmitter<any>(true);
  public itemsFieldLevelPerPage: ISampleFieldGridData[][] = [];
  public statisticByField: Map<number, IChartSet<IBarLineAreaChartData>[]>;
  public soilSampleFieldLayer: ApBaseVectorLayer;
  public currentDateTime: string;
  public currentFarm: IFarm;
  public reportName = '';
  public digitsAfterPoint = 0;
  public renderedMaps = 0;

  public soilGroupChartData: {
    values: number[],
    categories: string[],
    colorValues: string[],
    colorFunc: (value: any) => string,
  };

  protected readonly ApElementType = ApElementType;
  protected readonly ApElementOxydTypeEnum = ApElementOxydTypeEnum;
  protected readonly Math = Math;

  // Charts styling
  protected axisTextColor = '#212529';
  protected axisStyling: ValueAxisLabels = {
    font: '7px Arial, sans-serif',
    padding: 0,
    format: '{0}%'
  };

  protected categoryStyling: ValueAxisLabels = {
    margin: {top: -8},
    font: '7px Arial, sans-serif',
    padding: 0,
  };

  private readonly fieldsFirstPageCount = 21;
  private readonly fieldsPerPageCount = 29;

  public readonly samplesPerPageCount = 29;

  private _subscriptions: Subscription[] = [];
  private isSaving = false;
  private problematicFieldsAmount = 0;

  constructor(private mapStore: MapStore,
              private languageStore: LanguageStore,
              private loginStore: LoginStore,
              private dataProviders: TaskManagementDataProviders,
              private dateService: ApDateService,
              private settingsStore: SettingsStore,
              private roundNumericService: GetRoundNumericService,
              private translationStore: TranslationStore,
              protected elementService: GetElementService,
              protected nutrientService: ApNutrientService,
              protected sampleMethodService: SampleMethodService,
              private changeDetection: ChangeDetectorRef,
              private fieldStatisticHistogramService: FieldStatisticHistogramService,
              private landUseStore: LandUseStore,
              private notifyStore: NotifyStore,
              private errorHandlerService: ApErrorhandlerService) {
  }

  ngOnInit(): void {
    this.currentFarm = this.loginStore.Listen(l => l.farm).getValue().selectedFarm;
    const selectedLanguage = this.languageStore.Listen(l => l.selectedLanguage).getValue();
    this.currentDateTime = this.dateService.toFarmDate(new Date()).format(selectedLanguage.DateFormat + ' ' + selectedLanguage.TimeFormat);
    this.soilSampleFieldLayer = this.mapStore.Layers.SampleFieldLayer;

    const soilGroupLegend = SOIL_GROUP_LEGEND(this.translationStore.FindTranslationForSelectedLanguage.bind(this.translationStore));
    this.soilGroupChartData = {
      categories: soilGroupLegend.getValuesList().map(x => x.title),
      values: soilGroupLegend.getValuesList().map(_ => 1),
      colorValues: soilGroupLegend.getValuesList().map(x => x.color),
      colorFunc: value => this.soilGroupChartData.colorValues[value?.index]
    };
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  public createAndSaveReport(): void {
    // Lock for being executed twice.
    // Double execution might happen because render complete of Open layers
    // might get triggered again in rare cases.
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    this.currentFarm = this.loginStore.Listen(l => l.farm).getValue().selectedFarm;
    const selectedLanguage = this.languageStore.Listen(l => l.selectedLanguage).getValue();
    this.currentDateTime = this.dateService.toFarmDate(new Date()).format(selectedLanguage.DateFormat + ' ' + selectedLanguage.TimeFormat);

    this.digitsAfterPoint = this.settingsStore.FirstSetting.DigitsAfterDecimalPoint;
    this.itemsFieldLevelPerPage = [];
    this.statisticByField = new Map<number, IChartSet<IBarLineAreaChartData>[]>();
    this.renderedMaps = 0;
    this.problematicFieldsAmount = 0;

    // Sorting
    this.itemsFieldLevel.sort((a, b) => {
      if (a.FieldNumber !== b.FieldNumber) {
        return a.FieldNumber - b.FieldNumber;
      }

      if (a.FieldPart !== b.FieldPart) {
        return a.FieldPart - b.FieldPart;
      }

      return a.FieldName.localeCompare(b.FieldName);
    });

    this.itemsSampleTrackByField.sort((a, b) => {
      if (a.length === 0 || b.length === 0) {
        return 0;
      }

      if (a[0].SampleField.FieldNumber !== b[0].SampleField.FieldNumber) {
        return a[0].SampleField.FieldNumber - b[0].SampleField.FieldNumber;
      }

      if (a[0].SampleField.FieldPart !== b[0].SampleField.FieldPart) {
        return a[0].SampleField.FieldPart - b[0].SampleField.FieldPart;
      }

      return a[0].SampleField.FieldName.localeCompare(b[0].SampleField.FieldName);
    });

    // Sort by GPS Number of each grid
    this.itemsSampleTrackByField.forEach((samplesArray) => {
      samplesArray.sort((x, y) => x.GpsNumber - y.GpsNumber);
    });

    this.problematicFieldsAmount = this.itemsFieldLevel.length - this.itemsSampleTrackByField.length;

    if (this.problematicFieldsAmount > 0) {
      let fieldNames = '';
      this.itemsFieldLevel.forEach((field) => {
        if (this.itemsSampleTrackByField.findIndex(s => s[0].SampleField.Id === field.SourceItem.Id) < 0) {
          fieldNames += `${field.SourceItem.FieldNumber} - ${field.SourceItem.FieldPart} ${field.SourceItem.FieldName} `;
        }
      });

      const errorMessage = this.translationStore.FindTranslationForSelectedLanguage('Report__No_Sample_Records', [fieldNames]);

      this.notifyStore.addMessage({
        Counter: 1,
        DisplayTimeout: 5,
        ErrorKey: errorMessage,
        Level: Data.Api.Validation.ApValidationLevel.Warning,
        Parameters: [],
        UserName: null,
        UserId: null,
      });

      this.errorHandlerService.LogClientError(errorMessage, this.loginStore.SelectedFarmId, '', '', '');
    }

    // Pagination for the field level grid
    this.itemsFieldLevelPerPage.push(_.take(this.itemsFieldLevel, this.fieldsFirstPageCount));
    _.chunk(
      _.drop(this.itemsFieldLevel, this.fieldsFirstPageCount), this.fieldsPerPageCount)
      .forEach(c => {
        this.itemsFieldLevelPerPage.push(c);
      });

    this.reportName = `Soil_Sample_${this.currentFarm.FarmName}_${this.currentDateTime.toString()}`;

    this.itemsFieldLevel.forEach(sf =>
      this.statisticByField.set(sf.SourceItem.Id, this.fieldStatisticHistogramService.getHistogramStatistic([{
          Status: 1,
          FieldId: undefined,
          FieldName: sf.FieldName,
          AreaHa: sf.SourceItem.AreaHa ?? 0,
          Histograms: sf.SourceItem.Histograms
        }]).map(x => {
          // Value normalization in order to have percentage values (0 - 100)
          x.data?.data?.forEach((v, k) => x.data.data[k].value = x.data?.data[k].value * 100 / x.meta.areaSum);
          return x;
        })
      ));

    this.changeDetection.detectChanges();

    // trigger resize event on each chart to be rendered
    this.histograms.forEach(x => x.resize());

    // We need to wait for the resize to be completed. Alternative may be to listen to all
    // paneRender emitters and proceed when all of them are fired (haven't checked)
  }

  public getLandUseTranslation(value: number): string {
    const landuse = this.landUseStore.LandUse.Find(e => e.Id === value);
    if (!landuse) {
      return value.toString();
    }
    return this.translationStore.FindTranslationForSelectedLanguage(landuse.Key);
  }

  private saveReport(): void {
    try {
      // @ts-ignore
      const landscapePages = Array.from(this._report.element?.nativeElement?.querySelectorAll('.ap-landscape-page'));
      // @ts-ignore
      const pageBreaks = Array.from(this._report.element.nativeElement.querySelectorAll('.ap-report-page-break'));

      this._report.export().then(group => {
        landscapePages.forEach(page => {
          const pageIndex = pageBreaks.findIndex(el => el === page);
          if (pageIndex >= 0 && (pageIndex + 1) < group.children.length) {
            group.children[pageIndex + 1].options.set('pdf', {
              landscape: true, paperSize: this._report.paperSize, margin: this._report.margin
            });
          } else {
            console.log(`PDF Export : cannot find the correct page for a landscape {pageIndex}`);
          }
        });

        exportPDF(group).then(data => {
          // pdf generated => saving it and closing wizard
          saveAs(data, `${this.reportName}.pdf`);
          this.isSaving = false;
          this.loginStore.setLongOperationStatus(false);
          setTimeout(() => this.exportDone?.emit(), 0);
        });
      });
    } catch {
      this.loginStore.setLongOperationStatus(false);
    }
  }

  // Page markup support methods
  getSamplesNumber(item: ISampleFieldGridData): number {
    const result = this.itemsSampleTrackByField.find(st =>
      st.length > 0 && st.find(singleTrack => singleTrack.SampleField.Id === item.SourceItem.Id)
    )?.length;

    return result ? result : 0;
  }

  /**
   * Calculates if page break is needed for a FOLLOWING grid (you should pass i + 1)
   */
  calculateIfPageBreakNeededForSamples(fieldIndex: number, sampleIndex: number): boolean {
    if (sampleIndex >= this.itemsSampleTrackByField[fieldIndex].length) {
      return false;
    }

    const currentPage = Math.floor(sampleIndex / this.samplesPerPageCount);
    return currentPage > 0 && sampleIndex % this.samplesPerPageCount === 0;
  }

  public onRenderCompleted(): void {
    this.renderedMaps++;
    /* For each field :
       Soil Sample tracks layer + extras
       Soil Group Layer
       P Layer
       K Layer
       Mg Layer
       pH Layer
     */
    if (this.renderedMaps === (this.itemsFieldLevel.length - this.problematicFieldsAmount) * 6) {
      this.saveReport();
    }
  }

  getDistributionMapUrl(field: ISampleFieldGridData, elementId: number): string {
    const elements = ['p', 'k', 'mg', 'ph'];
    const url = this.mapStore.Layers.SoilSampleDistributionUrl
      .replaceAll(/_bu_\w+\//g, `_bu_sample_id_${elements[elementId]}/`)
      .replace(/\?params.+/, `?params={${field.SourceItem.Id}}`);
    return APP_CONFIGURATION.MapFactory.Address + url;
  }

  getFeatureUrl(field: ISampleFieldGridData, elementId: number): string {
    return APP_CONFIGURATION.MapFactory.Address + `/json/bu_contours/4326/${field.SourceItem.Id}/${elementId}?key=12345`;
  }

  getSampleTrackMapUrl(field: ISampleFieldGridData): string {
    return APP_CONFIGURATION.MapFactory.Address + `/json/sampletracks/4326/${field.SourceItem.Id}?key=12345`;
  }

  getSoilGroupMapUrl(field: ISampleFieldGridData): string {
    return APP_CONFIGURATION.MapFactory.Address + `/json/bu_soilgroup/4326/${field.SourceItem.Id}?key=12345`;
  }

  getFieldIndex(i: number): number {
    const sampleFieldId = this.itemsSampleTrackByField[i][0].SampleField.Id;
    return this.itemsFieldLevel.findIndex(x => x.SourceItem.Id === sampleFieldId);
  }

  oxideTransformation = (value: number, elem: ApElementOxydTypeEnum, method: string) => {
    return this.nutrientService.convertElementOxid(value, elem, method);
  }

  displayValueWithGreaterSign(value: number, isGreater: boolean, digitsToDisplay: number = 1): string {
    return isGreater ? `> ${this.roundNumericService.round(value, digitsToDisplay)}` : this.roundNumericService.round(value, digitsToDisplay);
  }

  displayAvgValueWithGreaterSign(value: number, isGreater: boolean): string {
    return this.displayValueWithGreaterSign(value, isGreater, value >= 10 ? 0 : 1);
  }

  displayValueByClass(value: number, valueClass: string): string {
    return this.displayValueWithGreaterSign(value, valueClass === 'E');
  }

  getAreaSum(itemsChunk: ISampleFieldGridData[]): number {
    return itemsChunk.reduce((sum, item) => sum + item.Area, 0);
  }

  getSamplesSum(itemsChunk: ISampleFieldGridData[]): number {
    return itemsChunk.reduce((sum, item) => sum + this.getSamplesNumber(item), 0);
  }
}
